// 8-bit style click sound using Web Audio API
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (required for autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
};

// Play a retro click sound
export const playClick = () => {
  try {
    const ctx = getAudioContext();
    
    // Ensure context is running
    if (ctx.state !== 'running') {
      ctx.resume().then(() => playClickSound(ctx));
    } else {
      playClickSound(ctx);
    }
  } catch (e) {
    console.log('Audio click not available:', e);
  }
};

const playClickSound = (ctx) => {
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // 8-bit style square wave
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.05);
    
    // Quick attack and decay for a snappy click
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.08);
  } catch (e) {
    console.log('Click sound error:', e);
  }
};

// Play a success/confirm sound
export const playSuccess = () => {
  try {
    const ctx = getAudioContext();
    
    if (ctx.state !== 'running') {
      ctx.resume().then(() => playSuccessSound(ctx));
    } else {
      playSuccessSound(ctx);
    }
  } catch (e) {
    console.log('Audio not available:', e);
  }
};

const playSuccessSound = (ctx) => {
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'square';
    // Rising tone for success
    oscillator.frequency.setValueAtTime(400, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  } catch (e) {
    console.log('Success sound error:', e);
  }
};

// Play a selection toggle sound
export const playToggle = () => {
  try {
    const ctx = getAudioContext();
    
    if (ctx.state !== 'running') {
      ctx.resume().then(() => playToggleSound(ctx));
    } else {
      playToggleSound(ctx);
    }
  } catch (e) {
    console.log('Audio not available:', e);
  }
};

const playToggleSound = (ctx) => {
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.05);
  } catch (e) {
    console.log('Toggle sound error:', e);
  }
};

// Play a hit sound (single/double/triple) - two quick rising notes
export const playHit = () => {
  try {
    const ctx = getAudioContext();
    const play = () => {
      [[523, 0], [784, 0.09]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.12);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.12);
      });
    };
    if (ctx.state !== 'running') { ctx.resume().then(play); } else { play(); }
  } catch (e) {
    console.log('Hit sound error:', e);
  }
};

// Play an out sound - descending buzz
export const playOut = () => {
  try {
    const ctx = getAudioContext();
    const play = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.32);
      gain.gain.setValueAtTime(0.22, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    };
    if (ctx.state !== 'running') { ctx.resume().then(play); } else { play(); }
  } catch (e) {
    console.log('Out sound error:', e);
  }
};

// Play a home run fanfare - 4-note rising arpeggio
export const playHomeRun = () => {
  try {
    const ctx = getAudioContext();
    const play = () => {
      [523, 659, 784, 1047].forEach((freq, i) => {
        const delay = i * 0.11;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
        const isLast = i === 3;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + (isLast ? 0.4 : 0.13));
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + (isLast ? 0.4 : 0.13));
      });
    };
    if (ctx.state !== 'running') { ctx.resume().then(play); } else { play(); }
  } catch (e) {
    console.log('Home run sound error:', e);
  }
};

export default { playClick, playSuccess, playToggle, playHit, playOut, playHomeRun };
